/* This example requires Tailwind CSS v2.0+ */
import { Fragment, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/solid";

/**
 * WideBlankModal — a wider modal shell (up to 4xl on large screens).
 *
 * On mobile it slides up from the bottom as a sheet; on desktop it appears
 * centered as a standard dialog. Provides a close button both inside (mobile)
 * and outside (desktop) the panel. Supports an optional title rendered via
 * `Dialog.Title`.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {function} props.setOpen - Setter to toggle modal visibility.
 * @param {string} [props.title] - Optional heading text rendered at the top of the panel.
 * @param {React.ReactNode} props.children - Content rendered inside the scrollable body.
 * @param {React.RefObject} [props.initialFocusRef] - Ref to the element that should receive focus when the modal opens. When omitted, Headless UI focuses the first tabbable child.
 * @param {React.RefObject} [props.returnFocusRef] - Ref to the element that should receive focus when the modal closes (typically the trigger that opened it). When omitted, no focus is restored.
 * @returns {JSX.Element} Animated wide modal dialog.
 */
export default function WideBlankModal({ open, setOpen, title, children, initialFocusRef, returnFocusRef, layerClassName = 'z-50' }) {
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (wasOpenRef.current && !open && returnFocusRef?.current) {
      returnFocusRef.current.focus({ preventScroll: true });
    }
    wasOpenRef.current = open;
  }, [open, returnFocusRef]);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className={`relative ${layerClassName}`}
        initialFocus={initialFocusRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900 bg-opacity-80 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto sm:overflow-y-auto">
          <div className="flex items-end sm:items-start justify-center min-h-full p-0 sm:p-0 text-center sm:pt-8">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-450"
              enterFrom="opacity-0 translate-y-full sm:translate-y-4 sm:-translate-y-5 sm:scale-80"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-full sm:translate-y-4 sm:-translate-y-5 sm:scale-80"
            >
              <div className="relative w-full max-w-full sm:max-w-2xl lg:max-w-4xl">
                {/* Close button - inside panel on mobile, outside on desktop */}
                <div className="hidden sm:block absolute right-0 -top-4 z-10">
                  <button
                    type="button"
                    aria-label="Close dialog"
                    className="rounded-full bg-gray-100 text-gray-500 p-2 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                    onClick={() => setOpen(false)}
                  >
                    <XIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                <Dialog.Panel className="relative bg-white rounded-t-2xl sm:rounded-lg px-4 py-4 sm:px-6 sm:py-8 text-left shadow-xl transform transition-all sm:my-8 w-full h-[92vh] sm:h-auto sm:max-h-[80vh] flex flex-col overflow-hidden">
                  {/* Mobile close button */}
                  <div className="sm:hidden flex justify-end mb-2 flex-shrink-0">
                    <button
                      type="button"
                      aria-label="Close dialog"
                      className="rounded-full bg-gray-100 text-gray-500 p-2 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-500"
                      onClick={() => setOpen(false)}
                    >
                      <XIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>

                  <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                    {title ? (
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900 flex-shrink-0"
                      >
                        {title}
                      </Dialog.Title>
                    ) : (
                      <Dialog.Title className="sr-only">Dialog</Dialog.Title>
                    )}
                    <div className="flex-1 min-h-0">{children}</div>
                  </div>
                </Dialog.Panel>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
